/** Reflection drip — one area reflection per day (spec §3.3).
 *  Pure logic; persistence and UI live in useReflectionDrip / ReflectionCard.
 *  Ordering note: the seeder's horizon-priority is not persisted (yet), so the
 *  proxy order is space sortOrder, then creation time = seeder selection order. */

export interface DripState {
  version: 1;
  /** space ids still unanswered, in show order (answered ids are removed) */
  queue: string[];
  /** space ids answered, in answer order — drives the "i of N" counter */
  answered: string[];
  /** spaceId -> local date key of the day it was skipped ("not today") */
  skippedOn: Record<string, string>;
}

export const DRIP_STORAGE_KEY = "home_reflection_drip_v1";

export const emptyDripState = (): DripState => ({
  version: 1,
  queue: [],
  answered: [],
  skippedOn: {},
});

/** Local calendar date key (not UTC) — a late-evening user is still "today". */
export const localDateKey = (d: Date = new Date()): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

interface DripSpaceInput {
  _id: string;
  sortOrder?: number;
  createdOn?: string | Date;
}

const spaceOrder = (a: DripSpaceInput, b: DripSpaceInput) => {
  const so = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
  if (so !== 0) return so;
  return new Date(a.createdOn ?? 0).getTime() - new Date(b.createdOn ?? 0).getTime();
};

/** Reconcile the queue with the live space list: keep existing order,
 *  append new spaces at the end, drop spaces that no longer exist. */
export function syncQueue(state: DripState, spaces: DripSpaceInput[]): DripState {
  const live = new Set(spaces.map((s) => s._id));
  const known = new Set([...state.queue, ...state.answered]);
  const kept = state.queue.filter((id) => live.has(id));
  const fresh = [...spaces]
    .sort(spaceOrder)
    .map((s) => s._id)
    .filter((id) => !known.has(id));
  return {
    ...state,
    queue: [...kept, ...fresh],
    answered: state.answered.filter((id) => live.has(id)),
  };
}

export interface CurrentReflection {
  spaceId: string;
  /** 1-based position in the drip ("Today's reflection · i of N") */
  index: number;
  total: number;
  /** the tomorrow-hook area, if any */
  nextSpaceId: string | null;
}

/** The (single) card to show today, or null (all answered / skipped today). */
export function currentReflection(state: DripState, today: string): CurrentReflection | null {
  const head = state.queue[0];
  if (!head) return null;
  if (state.skippedOn[head] === today) return null; // "not today" honored for the day
  return {
    spaceId: head,
    index: state.answered.length + 1,
    total: state.answered.length + state.queue.length,
    nextSpaceId: state.queue[1] ?? null,
  };
}

export function applyAnswer(state: DripState, spaceId: string): DripState {
  return {
    ...state,
    queue: state.queue.filter((id) => id !== spaceId),
    answered: state.answered.includes(spaceId) ? state.answered : [...state.answered, spaceId],
  };
}

/** "Not today": hide for the rest of today and re-queue the area at the end. */
export function applySkip(
  state: DripState,
  spaceId: string,
  today: string,
  /** The space that would surface next in the DISPLAY order — it waits until
   *  tomorrow. Omitted (undefined) = legacy behavior: the queue front after
   *  removal. Pass null explicitly when there is no next to defer. */
  deferNext?: string | null
): DripState {
  if (!state.queue.includes(spaceId)) return state;
  const rest = state.queue.filter((id) => id !== spaceId);
  const next = deferNext === undefined ? rest[0] : deferNext;
  return {
    ...state,
    queue: [...rest, spaceId],
    // the freshly surfaced next card also waits until tomorrow — never two in one day
    skippedOn: {
      ...state.skippedOn,
      [spaceId]: today,
      ...(next ? { [next]: today } : {}),
    },
  };
}

/** Every seeder welcome ends with a question — split it out for bold display. */
export function extractQuestion(text: string): { framing: string; question: string | null } {
  const stripped = text.replace(/[*_#>`]/g, "").replace(/\s+/g, " ").trim();
  const qEnd = stripped.lastIndexOf("?");
  if (qEnd === -1) return { framing: stripped, question: null };
  const upTo = stripped.slice(0, qEnd);
  const boundary = Math.max(
    upTo.lastIndexOf(". "),
    upTo.lastIndexOf("! "),
    upTo.lastIndexOf(": "),
    upTo.lastIndexOf("? ")
  );
  const before = boundary === -1 ? "" : stripped.slice(0, boundary + 1).trim();
  const after = stripped.slice(qEnd + 1).trim();
  const framing = [before, after].filter(Boolean).join(" ");
  const question = stripped.slice(boundary + 1, qEnd + 1).trim();
  return { framing, question: question.length > 0 ? question : null };
}
